import { useState } from "react";

import { useHistory, useParams } from "react-router-dom";
import { type CommitmentManagersModel } from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  useTheme,
} from "@mui/material";

import { ThreeDotsMenu } from "../../Components/ThreeDotsMenu/ThreeDotsMenu";

interface CommitmentActionsProps {
  commitmentId: string;
  docRef: ModelReference<CommitmentManagersModel>;
  onDelete?: () => void;
}

export const CommitmentActions = ({ commitmentId, docRef, onDelete }: CommitmentActionsProps) => {
  const history = useHistory();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { customerId } = useParams<{ customerId: string }>();
  const commitmentManagerPath = `/customers/${customerId}/commitment-manager`;
  const editPath = `${commitmentManagerPath}/${commitmentId}/edit`;
  const theme = useTheme();

  const handleDeleteConfirm = async () => {
    await docRef.delete();
    setIsDeleteDialogOpen(false);
    onDelete?.();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const threeDotsMenuOptions = [
    {
      label: "Edit commitment",
      key: "edit-commitment",
      action: () => {
        history.push(editPath);
      },
    },
    {
      label: "Delete commitment",
      key: "delete-commitment",
      color: theme.palette.error.main,
      action: () => {
        setIsDeleteDialogOpen(true);
      },
    },
  ];

  return (
    <>
      <ThreeDotsMenu
        options={threeDotsMenuOptions}
        size="medium"
        closeAfterSelect
        onClick={() => {}}
        data-cy="commitment-actions-menu"
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Commitment</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this commitment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleDeleteCancel} data-cy="commitment-cancel-delete">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
